/**
 * ② 분리+OCR · ③ 매칭이 쓰는 판정 — 순수 함수(화면과 떼어 검사한다).
 * 근거: BE-20260925-ocr-field-report-fixes.md §2-1·§2-3·§3(F9·F10·F11), dev 1e4eb15.
 */

/**
 * 실패 사유(ocr-status failed_pages[].reason) → 사람 말. 서버 분류(api/v2/ocr._failure_reason)를 그대로 옮긴다.
 *   overload  — 과부하·일시 오류. 시간이 지나 다시 읽으면 대개 성공(서버도 2·5·10·20·30분 간격으로 스스로 다시 시도)
 *   truncated — 출력 상한 잘림. 같은 입력이면 같은 실패 — 서버가 추론을 줄여 한 번 더 시도한 뒤에도 실패한 쪽이다
 *   error     — 그 밖
 * 모르는 코드는 원문 그대로 보인다(버리지 않는다).
 */
export const FAIL_REASON = {
  overload: { label: '과부하', how: '잠시 뒤 다시 읽으면 대개 성공합니다' },
  truncated: { label: '출력 잘림', how: '같은 입력이면 같은 실패가 납니다 — 지시로 읽는 방식을 바꿔 다시 읽으세요' },
  error: { label: '오류', how: null },
};

export function failReasonText(reason) {
  const m = FAIL_REASON[reason];
  return m || { label: String(reason ?? '오류'), how: null };
}

/**
 * 실패 쪽 목록 → 이번에 새로 「살아난」 쪽을 센다(F11 — 오너 결정 D4: 이 화면을 연 동안만 센다).
 *
 * 서버는 「마지막 매칭 뒤 새로 읽은 쪽」을 알려 주지 않는다(매칭 시각도 쪽의 읽은 시각도 응답에 없다).
 * 그래서 화면이 본 것만 센다: 한 번이라도 실패 목록에 있던 쪽이 목록에서 빠졌고, 그 순간 대기·처리 중인 쪽이
 * 하나도 없으면(다시 실패했다면 목록에 돌아왔을 것) 그 쪽은 새로 읽힌 것이다.
 * 대기 중에 목록에서 빠진 것은 아직 모른다 — 다시 대기열에 들어간 것일 뿐이다.
 *
 * state = { seen: Set<string>, recovered: Set<string> } 를 받아 **새 state** 를 돌려준다(원본을 바꾸지 않는다).
 */
export function trackRecoveredPages(state, failedPages, pendingCount) {
  const seen = new Set(state?.seen || []);
  const recovered = new Set(state?.recovered || []);
  if (!Array.isArray(failedPages)) return { seen, recovered }; // 옛 서버 — 목록이 없으면 세지 않는다
  const current = new Set(failedPages.map((fp) => String(fp?.page_id)));
  if ((Number(pendingCount) || 0) === 0) {
    seen.forEach((id) => {
      if (!current.has(id)) { recovered.add(id); seen.delete(id); }
    });
  }
  current.forEach((id) => { seen.add(id); recovered.delete(id); });
  return { seen, recovered };
}

/**
 * 실행 대상 쪽들에 걸린 지시를 모은다(F10 — 창이 「이 쪽들에는 전에 건 지시가 있다」를 보여 주고 지울지 고르게).
 *
 * filePages: { [file_id]: { indexes:number[], prompts:{[page_index]:string} } | null | undefined } — 쪽 목록 조회 결과
 * files:     detail.files — 대상 파일을 고르는 데 쓴다
 * fileId:    고른 파일(null 이면 전체)
 * pageRange: [s,e] 0-base 양끝 포함 | null(전체)
 *
 * 반환: [{ prompt, count }] (많은 순) · 대상 파일 중 하나라도 쪽 목록을 모르면 null — 모르면서 「없다」고 말하지 않는다.
 */
export function promptsForTargets(filePages, files, fileId, pageRange) {
  const list = Array.isArray(files) ? files : [];
  const targets = fileId != null ? list.filter((f) => String(f.file_id) === String(fileId)) : list;
  if (targets.length === 0) return [];
  const tally = new Map();
  for (const f of targets) {
    const view = filePages ? filePages[f.file_id] : undefined;
    if (!view || !view.prompts) return null;
    Object.entries(view.prompts).forEach(([idx, prompt]) => {
      const i = Number(idx);
      if (Array.isArray(pageRange) && (i < pageRange[0] || i > pageRange[1])) return;
      const v = String(prompt || '').trim();
      if (!v) return;
      tally.set(v, (tally.get(v) || 0) + 1);
    });
  }
  return [...tally.entries()]
    .map(([prompt, count]) => ({ prompt, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * 과부하 자동 재시도 대기 문구(§2-1 retrying · next_retry_at). 대기 중이 아니면 null.
 * nextRetryAt 은 UTC ISO 다. 지났거나 읽지 못하면 「곧」이라고만 말한다(지어낸 분 수를 보이지 않는다).
 */
export function retryWaitText(retrying, nextRetryAt, nowMs = Date.now()) {
  const n = Number(retrying) || 0;
  if (n <= 0) return null;
  const at = nextRetryAt ? Date.parse(nextRetryAt) : NaN;
  let when = '곧 다시 시도';
  if (Number.isFinite(at)) {
    const min = Math.ceil((at - nowMs) / 60000);
    if (min >= 1) when = `약 ${min}분 뒤 다시 시도`;
  }
  return `과부하로 자동 재시도 대기 ${n}쪽 · ${when}`;
}
